import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { css, useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { DefaultLink, Link } from '@hivebrite/design-system/Link';
import { Pill } from '@hivebrite/design-system/Pill';

import { getLocationValue } from 'hb-react/frontoffice/apps/Jobs/utils/templateUtils';
import JobDropdown from 'hb-react/frontoffice/apps/JobsV2/components/JobDropdown';
import { useJobContext } from 'hb-react/frontoffice/apps/JobsV2/contexts/JobContext';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { axiosWithSyntaxHandlers as axios } from 'hb-react/shared/utils/axios';
import decodeString from 'hb-react/shared/utils/decodeString';

const LinkToCompany = ({ organizationUrl, companyName, currentUser }) =>
  currentUser ? (
    <Link to={organizationUrl}>{decodeString(companyName)}</Link>
  ) : (
    <DefaultLink href={organizationUrl}>
      {decodeString(companyName)}
    </DefaultLink>
  );

LinkToCompany.propTypes = {
  organizationUrl: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  currentUser: PropTypes.object,
};

const Header = ({ urls }) => {
  const navigate = useNavigate();
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const queryClient = useQueryClient();
  const { topicId } = useTopicContext();
  const {
    company_name: companyName,
    company_id: companyId,
    id: jobId,
    jobKindName,
    locations,
    logo_thumb_url: logo,
    remote,
    title,
    user: jobUser,
    hasCurrentUserAlreadyApplied,
  } = useJobContext();

  const organizationUrl = companyId ? Routes.company_path(companyId) : null;

  const location = locations?.[0] || {};
  const locationValue = getLocationValue(location, remote);

  return (
    <>
      <Box>
        <Logo logo={logo} />
        <Box
          flexDirection="column"
          px={4}
          flex="1 1 100%"
          minHeight="60px"
          justifyContent="center"
        >
          <Box font="h2-semibold" mb={companyName ? 1 : 0} alignItems="center">
            {decodeString(title)}
            {hasCurrentUserAlreadyApplied && (
              <Pill
                minWidth="80px"
                height="24px"
                marginLeft="16px"
                iconName="circle-check"
                iconType={CATEGORIES.SOLID}
              >
                {s__('JobsV2|Applied')}
              </Pill>
            )}
          </Box>
          {organizationUrl ? (
            <LinkToCompany
              currentUser={currentUser}
              organizationUrl={organizationUrl}
              companyName={companyName}
            />
          ) : (
            <Box>{decodeString(companyName)}</Box>
          )}
        </Box>
        <JobDropdown
          currentNetwork={currentNetwork}
          currentUser={currentUser}
          jobId={jobId}
          jobUserId={jobUser?.id}
          onDeleteItem={async () => {
            await axios.delete(urls.deleteItem({ opportunityId: jobId }));
            queryClient.invalidateQueries({
              queryKey: [ITEMS_QUERY_KEYS.jobs.items, topicId],
            });
            queryClient.invalidateQueries({
              queryKey: [ITEMS_QUERY_KEYS.jobs.pinnedItems, topicId],
            });
            if (companyId) {
              queryClient.invalidateQueries({
                queryKey: [ITEMS_QUERY_KEYS.companies.jobs.items(companyId)],
              });
              queryClient.invalidateQueries({
                queryKey: [
                  ITEMS_QUERY_KEYS.companies.jobs.pinnedItems(companyId),
                ],
              });
            }
            navigate('..');
          }}
          editUrl={urls.editItem({ opportunityId: jobId })}
        />
      </Box>
      <Box mt={{ _: 4, l: 6 }} flexDirection="column">
        {locationValue && locationValue !== '-' && (
          <Box font="body-regular-asphalt" fontWeight="700">
            {locationValue}
          </Box>
        )}
        {jobKindName && <Box font="body-regular-asphalt">{jobKindName}</Box>}
      </Box>
    </>
  );
};

Header.propTypes = {
  urls: PropTypes.shape({
    deleteItem: PropTypes.func.isRequired,
    editItem: PropTypes.func.isRequired,
  }).isRequired,
};

const Logo = ({ logo }) => {
  const theme = useTheme();
  const size = 60;
  const borderRadiusCss = css`
    border-radius: ${theme.themeVariables.globalBorderRadius}px;
  `;

  return (
    <Box
      flex="0 0 auto"
      color="white"
      // bg="primary"
      height={size}
      width={size}
      alignItems="center"
      justifyContent="center"
      css={css`
        border: ${logo ? 'none' : `1px solid ${theme.colors.horizon}`};
        ${borderRadiusCss}
      `}
    >
      {logo ? (
        <Image src={logo} alt="" css={borderRadiusCss} />
      ) : (
        <FontAwesomeIcon
          iconName="briefcase"
          iconType="duotone"
          height={`${size - 24}px`}
          color={theme.colors.primary}
        />
      )}
    </Box>
  );
};

Logo.propTypes = {
  logo: PropTypes.string,
};

export default Header;
